import React, { useMemo } from 'react';
import { Box, Divider, TextField, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';

type AppearanceSettingsProps = {
  primaryColor: string;
  secondaryColor: string;
  onChange: (next: { primaryColor?: string; secondaryColor?: string }) => void;
};

function isHexColor(value: string): boolean {
  const v = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v);
}

function normalizeHex(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith('#')) return trimmed;
  return `#${trimmed}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return null;

  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function readableTextOn(bgHex: string): '#111827' | '#FFFFFF' {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return '#FFFFFF';
  // WCAG-ish luminance heuristic
  const l = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return l > 0.6 ? '#111827' : '#FFFFFF';
}

const PRESETS_PRIMARY = ['#4F46E5', '#2563EB', '#7C3AED', '#0EA5E9', '#10B981', '#F97316', '#EF4444', '#111827'];
const PRESETS_SECONDARY = ['#F3F4F6', '#FFFFFF', '#0B1220', '#111827', '#F8FAFC', '#ECFEFF', '#FFF7ED', '#FEE2E2'];

const ColorRow: React.FC<{
  label: string;
  helper: string;
  value: string;
  presets: string[];
  onValueChange: (next: string) => void;
}> = ({ label, helper, value, presets, onValueChange }) => {
  const normalized = normalizeHex(value);
  const swatch = isHexColor(normalized) ? normalized : '#4F46E5';

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {helper}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 1px 0 ${alpha('#000', 0.08)}`,
            bgcolor: swatch,
          }}
        >
          <input
            aria-label={`${label} picker`}
            type="color"
            value={isHexColor(normalizeHex(value)) ? normalizeHex(value) : swatch}
            onChange={(e) => onValueChange(e.target.value)}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          />
        </Box>

        <TextField
          label="Hex"
          value={value}
          onChange={(e) => onValueChange(normalizeHex(e.target.value))}
          placeholder="#4F46E5"
          size="small"
          sx={{ flex: 1 }}
          inputProps={{ spellCheck: false }}
          helperText={!value || isHexColor(normalizeHex(value)) ? ' ' : 'Enter a valid hex color like #4F46E5'}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {presets.map((c) => (
          <Tooltip key={c} title={c} arrow>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => onValueChange(c)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onValueChange(c);
              }}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 999,
                bgcolor: c,
                border: '1px solid',
                borderColor: alpha('#000', 0.14),
                boxShadow: `0 1px 0 ${alpha('#000', 0.06)}`,
                cursor: 'pointer',
                outline: 'none',
                '&:hover': { transform: 'translateY(-1px)' },
                '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(c, 0.28)}` },
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

const AppearanceSettings: React.FC<AppearanceSettingsProps> = ({ primaryColor, secondaryColor, onChange }) => {
  const safePrimary = useMemo(() => (isHexColor(normalizeHex(primaryColor)) ? normalizeHex(primaryColor) : '#4F46E5'), [primaryColor]);
  const safeSecondary = useMemo(() => (isHexColor(normalizeHex(secondaryColor)) ? normalizeHex(secondaryColor) : '#F3F4F6'), [secondaryColor]);
  const onPrimaryText = useMemo(() => readableTextOn(safePrimary), [safePrimary]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3, width: '100%' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick brand colors for your widget. We’ll show a live preview as you adjust them.
        </Typography>

        <Box sx={{ display: 'grid', gap: 2 }}>
          <ColorRow
            label="Primary color"
            helper="Header + accents (buttons, highlights)"
            value={primaryColor}
            presets={PRESETS_PRIMARY}
            onValueChange={(next) => onChange({ primaryColor: next })}
          />

          <Divider />

          <ColorRow
            label="Secondary color"
            helper="Widget background / surface color"
            value={secondaryColor}
            presets={PRESETS_SECONDARY}
            onValueChange={(next) => onChange({ secondaryColor: next })}
          />
        </Box>
      </Box>

      {/* Preview */}
      {/* <Box
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: `0 12px 30px ${alpha('#000', 0.08)}`,
          alignSelf: 'start',
        }}
      >
        <Box sx={{ px: 2, py: 1.5, bgcolor: safePrimary, color: onPrimaryText }}>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>AI Assistant</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Powered by Our Service
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: safeSecondary }}>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              p: 1.5,
              border: `1px solid ${alpha('#000', 0.08)}`,
              boxShadow: `0 6px 18px ${alpha('#000', 0.08)}`,
              maxWidth: 320,
            }}
          >
            <Typography variant="body2" sx={{ color: '#111827' }}>
              Hello! I’m your AI assistant. How can I help you today?
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                bgcolor: safePrimary,
                color: onPrimaryText,
                fontSize: 12,
                fontWeight: 700,
                boxShadow: `0 10px 18px ${alpha(safePrimary, 0.22)}`,
              }}
            >
              Send
            </Box>
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Preview
            </Typography>
          </Box>
        </Box>
      </Box> */}
    </Box>
  );
};

export default AppearanceSettings;


